import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Upload, FileSearch, Map, CheckCircle2, ArrowUpRight, BadgeDollarSign, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { IMAGES } from "@/data/site";
import { api } from "@/lib/api";

const STEPS = [
  { n: "01", title: "Upload your consent", desc: "Your official development consent PDF, plus optional plans, modifications or council correspondence. Tell us your council, address and where you're up to." },
  { n: "02", title: "We analyse your conditions", desc: "Every numbered condition is read and identified — pre-certificate, pre-commencement, during construction, occupation and ongoing — including anything needing another approval, a professional or a payment." },
  { n: "03", title: "Get your roadmap", desc: "A personalised, plain-English report: what each condition means, what to do, when, who's responsible, where to lodge and what it might cost — linked back to your actual consent." },
];

const REPORT_PARTS = [
  ["Your approval at a glance", "Address, council, DA number and what was approved — summarised in plain English."],
  ["Your immediate next steps", "A prioritised checklist of what to investigate or complete first."],
  ["Understanding your conditions", "Every condition explained individually, linked to its original number and page."],
  ["Your required applications", "Construction Certificate, Principal Certifier, s138 driveway approvals, contributions, occupation certificate — only the ones your consent actually calls for."],
  ["How to lodge each application", "The right authority, the right portal, what to prepare and how to confirm current fees."],
  ["Your project checklist", "Printable, interactive and organised by stage — from certification to ongoing obligations."],
];

const FUTURE = [
  ["Planner Review", "An experienced NSW planner checks unusual or complex conditions."],
  ["Book a Planning Consultation", "One-on-one help when you need to talk it through.", "/book"],
  ["Application Assistance", "Fixed-fee help preparing certificates, checklists and lodgements."],
];

const DaService = () => {
  const navigate = useNavigate();
  const [sampleLoading, setSampleLoading] = useState(false);

  const viewSample = async () => {
    setSampleLoading(true);
    try {
      const { data } = await api.get("/roadmap/sample");
      navigate(`/da-approved/report/${data.id}?sample=1`);
    } catch {
      toast.error("The sample report isn't available right now — please try again shortly.");
      setSampleLoading(false);
    }
  };

  return (
  <div data-testid="da-service-page">
    <Seo
      title="Your DA Is Approved. What Now?"
      description="Upload your NSW development consent and get a personalised, plain-English roadmap — your conditions explained, the applications you need, and every step from approval to completion."
    />
    <section className="bg-sand-light/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <FadeIn>
            <Eyebrow>After Your DA Is Approved</Eyebrow>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl text-teal tracking-tight leading-[1.05]">
              Your DA Is Approved. What Now?
            </h1>
            <p className="mt-5 text-base md:text-lg text-charcoal/75 leading-relaxed max-w-xl">
              Upload your development consent. Understand your conditions. Get your personalised
              roadmap from approval to completion — like sitting down with a knowledgeable planning
              officer who has actually read your approval.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/da-approved/upload"
                data-testid="da-upload-cta"
                className="inline-flex items-center gap-2 bg-teal text-ivory text-sm font-medium px-7 py-3.5 rounded-full hover:bg-teal-dark transition-colors"
              >
                <Upload className="w-4 h-4" /> Upload My DA Consent
              </Link>
              <span className="inline-flex items-center gap-2 text-sm font-medium text-teal" data-testid="da-price">
                <BadgeDollarSign className="w-5 h-5" /> A$69 per consent assessment
              </span>
              <button
                onClick={viewSample}
                disabled={sampleLoading}
                data-testid="da-sample-cta"
                className="inline-flex items-center gap-2 border border-teal text-teal text-sm font-medium px-7 py-3.5 rounded-full hover:bg-teal hover:text-ivory transition-colors disabled:opacity-60"
              >
                {sampleLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} See a Sample Roadmap
              </button>
            </div>
            <p className="mt-4 text-xs font-mono uppercase tracking-[0.14em] text-charcoal/55" data-testid="da-pilot-note">
              GST included · full refund if we can't read your consent reliably
            </p>
          </FadeIn>
        </div>
        <FadeIn delay={0.15} className="lg:col-span-5">
          <img
            src={IMAGES.suburban_renovation_cottage}
            alt="NSW home construction plans and site works after development approval"
            className="w-full aspect-[4/3] object-cover border border-cloud"
            loading="lazy"
          />
        </FadeIn>
      </div>
    </section>

    <section className="py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <Eyebrow>How it works</Eyebrow>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-teal tracking-tight">
            Your approval is only the beginning
          </h2>
        </FadeIn>
        <div className="mt-10 grid md:grid-cols-3 gap-5" data-testid="da-steps">
          {STEPS.map((s, i) => (
            <FadeIn key={s.n} delay={i * 0.06}>
              <div className="h-full bg-white border border-cloud p-7">
                <span className="font-serif text-3xl text-sand">{s.n}</span>
                <h3 className="mt-3 font-serif text-xl text-teal">{s.title}</h3>
                <p className="mt-2 text-sm text-charcoal/70 leading-relaxed">{s.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 lg:py-20 bg-sand-light/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <Eyebrow>Inside your report</Eyebrow>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-teal tracking-tight">
            Not a generic guide — your conditions, explained
          </h2>
        </FadeIn>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {REPORT_PARTS.map(([t, d], i) => (
            <FadeIn key={t} delay={i * 0.04}>
              <div className="h-full bg-white border border-cloud p-6">
                <CheckCircle2 className="w-5 h-5 text-teal/60" />
                <h3 className="mt-3 font-serif text-lg text-teal">{t}</h3>
                <p className="mt-2 text-sm text-charcoal/70 leading-relaxed">{d}</p>
              </div>
            </FadeIn>
          ))}
        </div>
        <FadeIn>
          <p className="mt-8 max-w-3xl text-sm text-charcoal/65 leading-relaxed">
            Accuracy protections: we preserve your original condition wording, link every
            explanation back to its condition number, flag anything ambiguous for professional
            review, and never invent fees, forms or requirements. If your consent can't be read
            reliably, we say so and offer manual review instead of guessing.
          </p>
        </FadeIn>
      </div>
    </section>

    <section className="py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <Eyebrow>Coming soon</Eyebrow>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-teal tracking-tight">
            Beyond the roadmap
          </h2>
        </FadeIn>
        <div className="mt-10 grid sm:grid-cols-3 gap-5" data-testid="da-future-services">
          {FUTURE.map(([t, d, link], i) => (
            <FadeIn key={t} delay={i * 0.05}>
              <div className="h-full bg-white border border-cloud p-6 flex flex-col">
                <span className="text-[10px] font-mono uppercase tracking-[0.14em] bg-sand-light text-charcoal/60 px-2.5 py-1 self-start">
                  {link ? "Available now" : "Coming soon"}
                </span>
                <h3 className="mt-4 font-serif text-lg text-teal">{t}</h3>
                <p className="mt-2 text-sm text-charcoal/70 leading-relaxed flex-1">{d}</p>
                {link && (
                  <Link to={link} className="mt-4 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.14em] text-teal">
                    Book now <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </div>
            </FadeIn>
          ))}
        </div>
        <FadeIn>
          <div className="mt-12 text-center">
            <Link
              to="/da-approved/upload"
              data-testid="da-upload-cta-bottom"
              className="inline-flex items-center gap-2 bg-teal text-ivory text-sm font-medium px-8 py-4 rounded-full hover:bg-teal-dark transition-colors"
            >
              <FileSearch className="w-4 h-4" /> Start My Roadmap <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-6 text-xs text-charcoal/55 max-w-xl mx-auto leading-relaxed">
              PLAN FIRST NSW provides independent planning education and administrative guidance —
              not statutory approval, certification or legal advice. Your documents are stored
              securely, never shared, and never used to train AI models. You can ask us to delete
              your documents at any time via hello@planfirstnsw.com.au.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>
  </div>
  );
};

export default DaService;
